import { gql, NetworkStatus } from '@apollo/client';
import { isEqual } from 'apollo-utilities';
import {
  LeadGenerator_NearbyBusinessesQuery,
  LeadGenerator_NearbyBusinessesQueryVariables,
} from '@/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWrappedQuery } from './use-wrapped-query';

export enum SortOrder {
  BEST_MATCH = 'best_match',
  DISTANCE = 'distance',
  RATING = 'rating',
}

export const GET_NEARBY_BUSINESSES = gql`
  query LeadGenerator_NearbyBusinesses(
    $categories: String
    $isCurrentRestaurant: Boolean
    $keyword: String
    $latitude: Float
    $location: String
    $longitude: Float
    $offset: Int
    $price: String
    $radius: String
    $limit: Int
    $sortBy: String
    $supplierUUID: uuid!
  ) {
    getNearbyBusinessesV2(
      categories: $categories
      is_current_restaurant: $isCurrentRestaurant
      keyword: $keyword
      latitude: $latitude
      location: $location
      longitude: $longitude
      offset: $offset
      price: $price
      radius: $radius
      limit: $limit
      sort_by: $sortBy
    ) {
      business_display_address
      business_name
      categories
      distance
      image_url
      latitude
      longitude
      phone_number
      restaurant_uuid
      yelp_business_id
    }

    businesses_by_pk(uuid: $supplierUUID) {
      address {
        country_code
        uuid
      }
      uuid
    }
  }
`;

export interface NearbyBusinessFilters {
  categories: string;
  isCurrentRestaurant?: boolean;
  latitude: number;
  longitude: number;
  price: string;
  radius: string | null;
  sortBy: SortOrder;
}

export const PAGE_SIZE = 20;
export const STARTING_OFFSET = 0;
const MAX_RESULTS = 100;

export const useNearbyBusinesses = (
  supplierUUID: string,
  filters: NearbyBusinessFilters,
  onHasResults?: (
    results: LeadGenerator_NearbyBusinessesQuery['getNearbyBusinessesV2'],
  ) => void,
) => {
  const offset = useRef(STARTING_OFFSET);
  const prevFilters = useRef<NearbyBusinessFilters>(undefined);
  const [canFetchMore, setCanFetchMore] = useState(true);

  const { data, error, fetchMore, loading, networkStatus, refetch } =
    useWrappedQuery<
      LeadGenerator_NearbyBusinessesQuery,
      LeadGenerator_NearbyBusinessesQueryVariables
    >(GET_NEARBY_BUSINESSES, {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (data?.getNearbyBusinessesV2 && onHasResults) {
          onHasResults(data.getNearbyBusinessesV2);
        }
      },
      skip: !filters.latitude || !filters.longitude,
      variables: {
        ...filters,
        limit: PAGE_SIZE,
        offset: STARTING_OFFSET,
        supplierUUID,
      },
    });

  const fetchingMore = networkStatus === NetworkStatus.fetchMore;

  // Check to see if the first page of results is already less
  // than one page size. If so, we're out of results.
  useEffect(() => {
    if (
      data?.getNearbyBusinessesV2 &&
      (data.getNearbyBusinessesV2.length < PAGE_SIZE ||
        data.getNearbyBusinessesV2.length >= MAX_RESULTS)
    ) {
      setCanFetchMore(false);
    } else if (!isEqual(prevFilters.current, filters)) {
      // If the filters have changed, reset the pagination
      setCanFetchMore(true);
    }
    prevFilters.current = filters;
  }, [data?.getNearbyBusinessesV2, filters]);

  // When filters change, reset the pagination to the first page
  useEffect(() => {
    offset.current = STARTING_OFFSET;
  }, [
    // React object equality will always say two filters Objects are unequal,
    // even when they have the same filters. So we must check each individually.
    filters.sortBy,
    filters.isCurrentRestaurant,
    filters.price,
    filters.latitude,
    filters.longitude,
    filters.categories,
  ]);

  // Fetch a new page of results
  // Sets the limit/offset state for what the next page of results will be,
  // because setting the state before calling fetchMore requires a re-render
  const paginateAndFetchMore = useCallback(() => {
    if (canFetchMore && !loading && !fetchingMore) {
      fetchMore({
        updateQuery: (prevResult, { fetchMoreResult }) => {
          // If we don't get a full page of results back, we're out of results
          setCanFetchMore(
            fetchMoreResult.getNearbyBusinessesV2.length === PAGE_SIZE,
          );

          // Merge the previous results and latest page of results
          const newResults = [
            ...prevResult.getNearbyBusinessesV2,
            ...fetchMoreResult.getNearbyBusinessesV2,
          ];

          const seen = new Set();
          const dedupedResults = newResults.filter((result) => {
            const duplicate = seen.has(result.yelp_business_id);
            seen.add(result.yelp_business_id);
            return !duplicate;
          });

          return {
            businesses_by_pk: prevResult.businesses_by_pk,
            getNearbyBusinessesV2: dedupedResults,
          };
        },
        variables: {
          ...filters,
          limit: PAGE_SIZE,
          offset: offset.current + PAGE_SIZE,
          supplierUUID,
        },
      });

      // Update offset with next page's length
      offset.current = offset.current + PAGE_SIZE;
    }
  }, [
    canFetchMore,
    fetchMore,
    fetchingMore,
    filters,
    loading,
    offset,
    supplierUUID,
  ]);

  return {
    canFetchMore,
    error,
    fetchingMore,
    fetchMore: paginateAndFetchMore,
    loading,
    nearbyBusinesses: data?.getNearbyBusinessesV2,
    retry: refetch,
    supplierCountryCode: data?.businesses_by_pk?.address.country_code,
  };
};
